import { finder } from '@medv/finder';

export function getDeepestTextNode(node: HTMLElement, level = 0, maxLevel = 2): HTMLElement | null {
  if (node.nodeType === Node.TEXT_NODE) {
    const textContent = node.textContent?.trim();
    if (textContent && /^\d+$/.test(textContent)) {
      return null;
    }
    return node;
  }
  if (level >= maxLevel) {
    return null;
  }
  for (const child of (node as any).childNodes) {
    const textNode = getDeepestTextNode(child, level + 1, maxLevel);
    if (textNode) {
      return textNode;
    }
  }
  return null;
}

export function getDeepestSvgNode(node: HTMLElement, level = 0, maxLevel = 2): HTMLElement | null {
  if (node.tagName && node.tagName.toLowerCase() === 'svg') {
    return node;
  }
  if (level >= maxLevel) {
    return null;
  }
  for (const child of (node as any).childNodes) {
    const svgNode = getDeepestSvgNode(child, level + 1, maxLevel);
    if (svgNode) {
      return svgNode;
    }
  }
  return null;
}

function matchSelector(node: HTMLElement, selector: string) {
  try {
    const match = node.matches(selector);
    return match;
  } catch {
    return false;
  }
}

export function findMatchingSelector(
  node: HTMLElement,
  selectors: string[],
  depth = 0
): null | { matchedSelector: string; finalTarget: HTMLElement } {
  if (!selectors || depth > 2) return null;

  for (const selector of selectors) {
    if (matchSelector(node, selector)) {
      return { matchedSelector: selector, finalTarget: node };
    }
  }

  if (node.parentElement) {
    return findMatchingSelector(node.parentElement, selectors, depth + 1);
  }

  return null;
}

export function getAutoBtm(btm: string) {
  const parts = btm.split('.');
  if (parts.length === 4) {
    parts[parts.length - 1] = 'btm_auto_d';
    const modifiedString = parts.join('.');
    return modifiedString;
  } else {
    return btm;
  }
}

export function getClassName(node?: Element) {
  if (!node) return;
  if (node.tagName.toLowerCase() === 'svg' || node.tagName.toLowerCase() === 'path') {
    const svgClassName = getSVGClassName((node as any as SVGSVGElement).className.baseVal);
    if (svgClassName) return svgClassName;
  }
  const classNames = node.className;
  const classArray = classNames.split(' ');
  const filteredClassArray = classArray.filter(
    (className) => !className.includes('arco') && !className.includes('m4b')
  );
  const filteredClassNames = filteredClassArray.join(' ');
  return filteredClassNames;
}

export function getSVGClassName(className?: string) {
  if (!className) return;
  const regex = /\b\w+-icon-\w+\b/g;
  const matches = className.match(regex);

  if (matches) {
    return matches[0];
  }
}

export const getStarlingKey = (text?: string | null, getReverseIndex?: () => Record<string, Set<string>>) => {
  if (!getReverseIndex || !text) return undefined;
  const reverseIndex = getReverseIndex();
  if (reverseIndex[text]) {
    const arr = Array.from(reverseIndex[text]);
    const [lang, key] = arr[arr.length - 1].split('@');
    return {
      lang,
      key
    };
  }
  return undefined;
};

export const checkSVG = (node: HTMLElement) => {
  let finalTarget, text;
  let finalNode = node;

  if (node.tagName.toLowerCase() === 'path') {
    const svgElement = node.closest('svg');
    if (svgElement) {
      finalNode = svgElement as any;
    }
  }

  const svgClassName = getSVGClassName((finalNode as any as SVGSVGElement).className.baseVal); // 使用 baseVal 获取 className 的字符串值
  const target = finalNode.closest('[data-tid]');
  const tid = target?.getAttribute('data-tid');
  // 判断是否被 m4b 组件包裹
  if (target && tid && M4B_CLICK_WHITE_LIST.includes(tid)) {
    finalTarget = target;
    const m4bTextNode = getDeepestTextNode(target as HTMLElement);
    text = m4bTextNode?.nodeValue ?? target.textContent;
  } else {
    const uidTarget = node.closest('[data-uid]');
    const uid = uidTarget?.getAttribute('data-uid');
    if (uidTarget && uid) {
      finalTarget = uidTarget;
      const m4bTextNode = getDeepestTextNode(uidTarget as HTMLElement);
      text = m4bTextNode?.nodeValue ?? uidTarget.textContent;
    } else {
      finalTarget = finalNode;
    }
  }

  return {
    svgClassName,
    finalTarget,
    text
  };
};

const getComponentInfo = (node: HTMLElement, target: Element | null) => {
  let text, svgClassName;
  const targetNode = getDeepestTextNode(node);
  if (targetNode) {
    // 点击位置有文本
    text = targetNode.nodeValue ?? '';
  } else {
    // 点击位置有 svg
    const svgNode = getDeepestSvgNode(node);
    if (svgNode) {
      svgClassName = getSVGClassName((svgNode as any as SVGSVGElement).className.baseVal);
    }
  }
  const finalTarget = target;
  return {
    text,
    svgClassName,
    finalTarget
  };
};

export const checkM4B = (node: HTMLElement) => {
  const target = node.closest('[data-tid]');
  const tid = target?.getAttribute('data-tid');

  // 点击位置在 m4b 组件内
  if (target && tid && M4B_CLICK_WHITE_LIST.includes(tid)) {
    const m4bInfo = getComponentInfo(node, target);
    return m4bInfo;
  } else {
    const uidTarget = node.closest('[data-uid]');
    const uid = uidTarget?.getAttribute('data-uid');
    if (uidTarget && uid) {
      const uidInfo = getComponentInfo(node, uidTarget);
      return uidInfo;
    }
  }
  return { finalTarget: undefined, svgClassName: undefined, text: undefined };
};

export const checkHTMLTag = (node: HTMLElement) => ({
  finalTarget: node,
  text: node.innerText
});

export const M4B_CLICK_WHITE_LIST = [
  'm4b_button',
  'm4b_button_toggle_button',
  'm4b_dropdown_button',
  'm4b_menu',
  'm4b_menu_submenu',
  'm4b_menu_item',
  'm4b_dropdown_menu',
  'm4b_dropdown_menu_item',
  'm4b_dropdown_submenu',
  'm4b_link',
  'm4b_anchor_link',
  'm4b_select',
  'm4b_select_option',
  'm4b_radio_group',
  'm4b_radio',
  'm4b_checkbox',
  'm4b_checkbox_group',
  'm4b_breadcrumb_item',
  'm4b_switch',
  'm4b_tabs'
];

export function getCssSelector(element: Element | null): string | undefined {
  try {
    if (!element || !(element instanceof Element)) return;
    const selector = finder(element);
    return selector;
  } catch {
    return undefined; // 返回 undefined 表示无法生成 CSS 选择器
  }
}

export function getLatestDataUid(node: HTMLElement | null): string | null {
  let current: HTMLElement | null = node;

  while (current) {
    const dataUid = current.getAttribute('data-uid');
    if (dataUid) {
      return dataUid;
    }
    current = current.parentElement;
  }

  return null;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6IiIsIm5hbWVzIjpbXSwic291cmNlcyI6W10sInNvdXJjZXNDb250ZW50IjpbXX0=